import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useBacktest, useSymbolBacktests } from "../bots/backtest-queries.js";
import { useBots } from "../bots/queries.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { useAllTrades, useTrades } from "../trades/queries.js";
import { useAccounts } from "../settings/queries.js";
import { backtestEvidenceRows } from "./chart-models.js";
import { useBacktestChart, useTickerBars } from "./chart-queries.js";
import { MarketChart } from "./market-chart.js";
import { EquityChart } from "./equity-chart.js";
import { ManualOrderDialog } from "./manual-order-dialog.js";
import { LiveExecutions } from "./live-executions.js";
import { chartFillsFromExecutions, liveExecutionRows } from "./live-execution-models.js";
import { useTickers } from "./queries.js";
import { TickerSymbolPicker } from "./ticker-symbol-picker.js";

type LiveInterval = "1m" | "5m" | "15m" | "1h" | "1d";
const liveIntervals: readonly LiveInterval[] = ["1m", "5m", "15m", "1h", "1d"];

function localDate(value: string): string {
  return value === "" ? "Time unavailable" : new Date(value).toLocaleString();
}

function BacktestEvidence({
  backtestId,
  from,
  to,
}: {
  readonly backtestId: string;
  readonly from: string;
  readonly to: string;
}) {
  const chart = useBacktestChart(backtestId, true, { from, to });
  const [selectedRowId, setSelectedRowId] = useState<string>();
  if (chart.isLoading)
    return (
      <div role="status">
        <ResourceState
          kind="loading"
          title="Loading backtest chart"
          description={`Loading the entire backtest period automatically. ${chart.progress?.requests ?? 0} requests loaded${chart.progress === undefined ? "" : ` through ${localDate(chart.progress.through)}`}.`}
        />
      </div>
    );
  if (chart.error !== null || chart.data === undefined)
    return (
      <>
        <ResourceState
          kind="error"
          title="Backtest chart unavailable"
          description="The entire backtest period could not be loaded. Incomplete evidence is not shown as a complete chart."
          action={
            <button
              className="button button--quiet"
              type="button"
              onClick={() => void chart.refetch()}
            >
              Retry full-period chart
            </button>
          }
        />
      </>
    );
  const evidenceRows = backtestEvidenceRows(chart.data.fills, chart.data.decisions);
  const selectedRow = evidenceRows.find((row) => row.id === selectedRowId);
  return (
    <>
      <p className="field-hint" aria-label="Backtest chart period">
        Full backtest period: {localDate(from)} – {localDate(to)} · {chart.data.bars.length} bars.
        Times above use your local timezone. Zoom or pan to inspect any date.
      </p>
      {chart.data.barSource === "reconstructed" ? (
        <div className="truth-banner truth-banner--local-projection">
          <strong>Legacy run bars reconstructed</strong>
          <span>
            This run predates embedded chart bars. Candles were reloaded from its frozen market-data
            request; fills and decisions remain the original immutable result.
          </span>
        </div>
      ) : null}
      <MarketChart
        bars={chart.data.bars}
        fills={chart.data.fills}
        {...(selectedRow === undefined ? {} : { focusAt: selectedRow.at })}
        {...(selectedRow?.fill === undefined ? {} : { selectedFillId: selectedRow.fill.id })}
      />
      <EquityChart points={chart.data.equityCurve} />
      <section className="chart-evidence-panel">
        <div className="connection-heading">
          <div>
            <h2>Executions and decisions</h2>
            <p className="section-description">
              Executions and signals across the entire backtest period. Select a row to focus the
              chart.
            </p>
          </div>
          <span className="status-pill status-pill--completed">
            {chart.data.fills.length} fills · {chart.data.decisions.length} signals
          </span>
        </div>
        {evidenceRows.length === 0 ? (
          <ResourceState
            kind="empty"
            title="No trade evidence"
            description="There are no executions or signals in this backtest period."
          />
        ) : (
          <div className="evidence-table-scroll">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th scope="col">Time</th>
                  <th scope="col">Action</th>
                  <th scope="col">Execution</th>
                  <th scope="col">Decision path</th>
                </tr>
              </thead>
              <tbody>
                {evidenceRows.map((row) => (
                  <tr
                    aria-selected={selectedRowId === row.id}
                    key={row.id}
                    onClick={() => setSelectedRowId(row.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedRowId(row.id);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td>{localDate(row.at)}</td>
                    <td>
                      <strong>{row.action.replaceAll("-", " ")}</strong>
                    </td>
                    <td>
                      {row.fill === undefined
                        ? "Not executed"
                        : `${row.fill.quantity} shares @ $${row.fill.priceUsd} · fee $${row.fill.feeUsd}`}
                    </td>
                    <td>{row.decision?.reason ?? "Decision path unavailable"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export function TickerDetailPage({ symbol }: { readonly symbol: string }) {
  const tickers = useTickers();
  const bots = useBots({ symbol });
  const accounts = useAccounts();
  const backtests = useSymbolBacktests(symbol);
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const view = search.get("view") === "backtesting" ? "backtesting" : "live";
  const requestedOrderId = search.get("orderId") ?? "";
  const [selectedFillId, setSelectedFillId] = useState<string>();
  const [orderOpen, setOrderOpen] = useState(false);
  const requestedBacktestId = search.get("backtestId") ?? "";
  const requestedBacktest = useBacktest(
    requestedBacktestId,
    view === "backtesting" && requestedBacktestId !== "",
  );
  const requestedInterval = search.get("interval");
  const interval: LiveInterval = liveIntervals.includes(requestedInterval as LiveInterval)
    ? (requestedInterval as LiveInterval)
    : "1m";
  const completedRuns = useMemo(() => {
    const listed = (backtests.data ?? []).filter((run) => run.status === "completed");
    const requested = requestedBacktest.data;
    return requested?.status === "completed" &&
      requested.request.symbol.toUpperCase() === symbol.toUpperCase() &&
      !listed.some((run) => run.id === requested.id)
      ? [requested, ...listed]
      : listed;
  }, [backtests.data, requestedBacktest.data, symbol]);
  const selectedRun =
    completedRuns.find((run) => run.id === requestedBacktestId) ?? completedRuns[0];
  const trades = useAllTrades("", { symbol }, view === "live");
  const linkedTrade = useTrades(
    "",
    { symbol, orderId: requestedOrderId },
    view === "live" && requestedOrderId !== "",
  );
  const executions = useMemo(
    () => liveExecutionRows([...(trades.data?.items ?? []), ...(linkedTrade.data?.items ?? [])]),
    [trades.data, linkedTrade.data],
  );
  const linkedExecution = useMemo(
    () => liveExecutionRows(linkedTrade.data?.items ?? [])[0],
    [linkedTrade.data],
  );
  const focusedExecution =
    executions.find((item) => item.fillId === selectedFillId) ?? linkedExecution;
  const chartFills = useMemo(() => chartFillsFromExecutions(executions), [executions]);
  const liveBars = useTickerBars(
    symbol,
    interval,
    view === "live" && trades.isSuccess && (requestedOrderId === "" || linkedTrade.isSuccess),
    executions.at(-1)?.occurredAt,
  );
  const updateSearch = (values: Readonly<Record<string, string | undefined>>) => {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(values)) {
      if (value === undefined) next.delete(key);
      else next.set(key, value);
    }
    setSearch(next, { replace: true });
  };

  return (
    <section className="page-stack ticker-workspace" aria-label={`${symbol} chart workspace`}>
      <div className="content-card symbol-toolbar" aria-label="Symbol and chart controls">
        <TickerSymbolPicker
          symbol={symbol}
          suggestions={(tickers.data ?? []).map((item) => item.symbol)}
          onOpen={(value) => void navigate(`/tickers/${encodeURIComponent(value)}?view=${view}`)}
        />
        <div className="tab-list" role="tablist" aria-label="Ticker chart mode">
          <button
            aria-selected={view === "live"}
            onClick={() => updateSearch({ backtestId: undefined, view: "live" })}
            role="tab"
            type="button"
          >
            Live
          </button>
          <button
            aria-selected={view === "backtesting"}
            onClick={() => updateSearch({ view: "backtesting" })}
            role="tab"
            type="button"
          >
            Backtesting
          </button>
        </div>
        {view === "live" ? (
          <div className="interval-selector" aria-label="Live chart interval">
            {liveIntervals.map((value) => (
              <button
                aria-pressed={interval === value}
                key={value}
                onClick={() => updateSearch({ interval: value })}
                type="button"
              >
                {value}
              </button>
            ))}
          </div>
        ) : (
          <div className="field-stack run-selector">
            <label htmlFor="backtest-select">Completed backtest</label>
            <select
              disabled={completedRuns.length === 0}
              id="backtest-select"
              onChange={(event) => updateSearch({ backtestId: event.currentTarget.value })}
              value={selectedRun?.id ?? ""}
            >
              {completedRuns.length === 0 ? <option value="">No completed runs</option> : null}
              {completedRuns.map((run) => (
                <option key={run.id} value={run.id}>
                  {localDate(run.completedAt ?? run.updatedAt)} · revision {run.revisionNumber}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="symbol-toolbar__actions">
          {view === "backtesting" && selectedRun !== undefined ? (
            <Link
              className="button button--quiet"
              to={`/bots/${selectedRun.botId}/backtests/${selectedRun.id}`}
            >
              Run details
            </Link>
          ) : null}
          <Link className="button button--quiet" to={`/bots?symbol=${encodeURIComponent(symbol)}`}>
            View Bots
          </Link>
          {view === "live" ? (
            <button
              className="button button--primary"
              onClick={() => setOrderOpen(true)}
              type="button"
            >
              Create Order
            </button>
          ) : null}
        </div>
      </div>

      <div className="content-card section-stack chart-workspace-card">
        {view === "live" ? (
          trades.error !== null || (requestedOrderId !== "" && linkedTrade.error !== null) ? (
            <ResourceState
              kind="error"
              title="Live trading period unavailable"
              description="The complete execution history could not be loaded, so the full trading period cannot be determined."
              action={
                <button
                  className="button button--quiet"
                  type="button"
                  onClick={() => {
                    void trades.refetch();
                    if (requestedOrderId !== "") void linkedTrade.refetch();
                  }}
                >
                  Retry live history
                </button>
              }
            />
          ) : trades.isLoading ||
            (requestedOrderId !== "" && linkedTrade.isLoading) ||
            liveBars.isLoading ? (
            <ResourceState
              kind="loading"
              title="Loading live chart"
              description={`Loading the full recorded trading period automatically. ${liveBars.progress?.requests ?? 0} market-data requests loaded${liveBars.progress === undefined ? "" : ` through ${localDate(liveBars.progress.through)}`}.`}
            />
          ) : liveBars.error !== null || liveBars.data === undefined ? (
            <ResourceState
              kind="error"
              title="Live chart unavailable"
              description={`The trading period could not be loaded from Alpaca.${liveBars.error === null ? "" : ` ${liveBars.error.message}`}`}
              action={
                <button
                  className="button button--quiet"
                  type="button"
                  onClick={() => void liveBars.refetch()}
                >
                  Retry full-period chart
                </button>
              }
            />
          ) : liveBars.data.bars.length === 0 ? (
            <ResourceState
              kind="empty"
              title="No bars in this period"
              description="No Alpaca bars were returned for the trading period and selected interval."
            />
          ) : (
            <>
              <MarketChart
                bars={liveBars.data.bars}
                fills={chartFills}
                {...(focusedExecution === undefined
                  ? {}
                  : {
                      focusAt: focusedExecution.occurredAt,
                      selectedFillId: focusedExecution.fillId,
                    })}
              />
              <p className="field-hint" aria-label="Live chart period">
                {executions.length === 0
                  ? "Recent market window (no recorded TradingApp fills)"
                  : "Full recorded TradingApp trading period through the market-data cutoff"}
                : {localDate(liveBars.data.range.from)} – {localDate(liveBars.data.range.to)} ·{" "}
                {liveBars.data.bars.length} bars · {liveBars.data.dataVersion} · refreshes every
                minute. Alpaca SIP market data is delayed by 16 minutes for free-plan access. All
                execution history is loaded automatically. Times above use your local timezone.
              </p>
              {focusedExecution === undefined ? null : (
                <button
                  className="button button--quiet"
                  onClick={() => {
                    setSelectedFillId(undefined);
                    updateSearch({ orderId: undefined });
                  }}
                  type="button"
                >
                  Show full period
                </button>
              )}
            </>
          )
        ) : backtests.isLoading && requestedBacktest.data === undefined ? (
          <ResourceState
            kind="loading"
            title="Loading backtests"
            description="Reading completed runs for this symbol."
          />
        ) : selectedRun === undefined ? (
          <ResourceState
            kind="empty"
            title="No completed backtests"
            description="Launch a backtest from one of this symbol's Bots to populate the chart."
            action={
              bots.data?.[0] === undefined ? undefined : (
                <Link className="button button--quiet" to={`/bots/${bots.data[0].id}#backtests`}>
                  Open Bot
                </Link>
              )
            }
          />
        ) : (
          <BacktestEvidence
            key={selectedRun.id}
            backtestId={selectedRun.id}
            from={selectedRun.request.from}
            to={selectedRun.request.to}
          />
        )}
      </div>
      {view === "live" ? (
        <LiveExecutions
          rows={executions}
          accountNames={
            new Map((accounts.data ?? []).map((account) => [account.id, account.displayName]))
          }
          botNames={new Map((bots.data ?? []).map((bot) => [bot.id, bot.name]))}
          {...(focusedExecution === undefined ? {} : { selectedFillId: focusedExecution.fillId })}
          onSelect={setSelectedFillId}
          loading={trades.isLoading || (requestedOrderId !== "" && linkedTrade.isLoading)}
          error={trades.error !== null || (requestedOrderId !== "" && linkedTrade.error !== null)}
        />
      ) : null}
      <ManualOrderDialog symbol={symbol} open={orderOpen} onClose={() => setOrderOpen(false)} />
    </section>
  );
}
